import TileWrapper from "ds/components/Tile/Wrapper";
import TileContent from "ds/components/Tile/Content";
import IconEmphasized from "ds/components/IconEmphasized";
import TileTitle from "ds/components/Tile/Title";
import { VcsIntegrationCheck, VcsProvider, VcsIntegrationTestStatus } from "types/generated";
import BadgeNext from "ds/components/BadgeNext";
import Banner from "ds/components/Banner";
import FeedbackActions from "ds/components/Feedback/Actions";
import Link from "ds/components/Link";
import { HUMANIZE_PROVIDER } from "constants/vcs_providers";

import { generateStatusCheckTitle } from "./helpers";
import { VCS_CHECK_TYPE_LABELS, VCS_CHECK_TYPE_ICONS } from "./constants";
import { showStatusCheckAppEventsDrawer } from "../AppEventsDrawer";
import { showStatusCheckPermissionsDrawer } from "../PermissionsDrawer";
import StatusCheckFixButton from "./FixButton";
import { GithubPermissions } from "../types";
import styles from "./styles.module.css";

type StatusCheckItemProps = {
  check: VcsIntegrationCheck;
  provider: VcsProvider;
  appUrl?: string;
};

const StatusCheckItem = ({ check, provider, appUrl }: StatusCheckItemProps) => {
  const humanizedProvider = HUMANIZE_PROVIDER[provider];
  const isStatusSuccess = check.status === VcsIntegrationTestStatus.Success;
  const hasConnectionErrorExplanation = check.metadata && check.metadata.reason;
  const hasSuccessGithubAppEvents =
    isStatusSuccess && check.metadata?.githubAppEvents && check.metadata.githubAppEvents.length > 0;
  const hasSuccessPermissions =
    isStatusSuccess && check.metadata?.permissions && check.metadata.permissions.length > 0;
  const hasFailedGithubAppEvents =
    !isStatusSuccess &&
    check.metadata?.githubAppEvents &&
    check.metadata.githubAppEvents.length > 0;
  const hasFailedPermissions =
    !isStatusSuccess && check.metadata?.permissions && check.metadata.permissions.length > 0;

  const handleShowAppEvents = () => {
    showStatusCheckAppEventsDrawer({
      items: check.metadata?.githubAppEvents || [],
    });
  };

  const handleShowPermissions = () => {
    showStatusCheckPermissionsDrawer({
      items: (check.metadata?.permissions || []) as GithubPermissions[],
    });
  };

  return (
    <TileWrapper direction="column" gap="large">
      <TileContent gap="small" direction="row" align="center">
        <IconEmphasized icon={VCS_CHECK_TYPE_ICONS[check.type]} />
        <TileTitle>{VCS_CHECK_TYPE_LABELS[check.type]}</TileTitle>
        <BadgeNext
          variant={isStatusSuccess ? "green" : "red"}
          type="strong"
          text={check.status}
          className={styles.badge}
        />
      </TileContent>
      <TileContent gap="small" direction="row" align="center">
        <Banner
          variant={isStatusSuccess ? "success" : "danger"}
          fullWidth
          title={generateStatusCheckTitle(check, humanizedProvider)}
        >
          {/* metadata.reason field contains explanation for connection errors.
          It is undefined when there is no error of this kind. */}
          {hasConnectionErrorExplanation && <>{check?.metadata?.reason}</>}
          {/* When gh events are set correctly we show the list of events */}
          {hasSuccessGithubAppEvents && (
            <FeedbackActions>
              <Link onPress={handleShowAppEvents}>Events list</Link>
            </FeedbackActions>
          )}
          {/* When gh permissions are set correctly we show the list of permissions */}
          {hasSuccessPermissions && (
            <FeedbackActions>
              <Link onPress={handleShowPermissions}>Permissions list</Link>
            </FeedbackActions>
          )}
          {/* If the check is not successful, we show the fix button */}
          {hasFailedGithubAppEvents && appUrl && (
            <>
              You might need to fix that otherwise Spacelift will not be able to send events.
              <StatusCheckFixButton appUrl={appUrl} />
            </>
          )}
          {/* If the check is not successful, we show the fix button for permissions */}
          {hasFailedPermissions && appUrl && (
            <>
              You might need to fix that otherwise Spacelift will not be able to perform actions for
              you on GitHub.
              <StatusCheckFixButton appUrl={appUrl} />
            </>
          )}
        </Banner>
      </TileContent>
    </TileWrapper>
  );
};

export default StatusCheckItem;
